import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Card } from '@/components/ui/Card';
import { useModelInfo, useUpdateModel } from '@/api/hooks/useModels';
import { ApiException } from '@/api/client';

export function ModelEditPage() {
  const { id = '' } = useParams<{ id: string }>();
  const decodedId = decodeURIComponent(id);
  const navigate = useNavigate();
  const { data, isLoading, error } = useModelInfo(decodedId);
  const update = useUpdateModel();

  const [modelName, setModelName] = useState('');
  const [model, setModel] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [apiBase, setApiBase] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (data) {
      setModelName(data.id);
      const params = data.litellm_params ?? {};
      setModel(typeof params.model === 'string' ? params.model : '');
      setApiBase(typeof params.api_base === 'string' ? params.api_base : '');
      // Note: api_key is left blank — the form lets the user supply a new key
      // to rotate. The existing key isn't fetched in plaintext by upstream,
      // so we don't display it.
    }
  }, [data]);

  if (isLoading) {
    return (
      <div
        style={{
          height: 384,
          background: '#e2e8f0',
          borderRadius: 8,
          animation: 'pulse 2s infinite',
        }}
      />
    );
  }

  if (error || !data) {
    return (
      <Card>
        <div style={{ padding: 24, color: '#991b1b', fontSize: 13 }}>
          Failed to load model.{' '}
          <Button variant="outline" onClick={() => navigate('/models')}>
            Back
          </Button>
        </div>
      </Card>
    );
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!modelName) errs.model_name = 'Required';
    if (!model) errs.model = 'Required';
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setSubmitError(null);
    update.mutate(
      {
        model_name: modelName,
        litellm_params: {
          model,
          api_key: apiKey || undefined,
          api_base: apiBase || undefined,
        },
      },
      {
        onSuccess: () => navigate(`/models/${encodeURIComponent(modelName)}`),
        onError: (e) =>
          setSubmitError(e instanceof ApiException ? e.message : 'Update failed'),
      },
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 600 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Edit Model</h1>
      <Card>
        <form
          onSubmit={handleSubmit}
          style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
        >
          {submitError && (
            <div
              style={{
                padding: 12,
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 6,
                color: '#991b1b',
                fontSize: 13,
              }}
            >
              {submitError}
            </div>
          )}
          <FormField label="Model Name" htmlFor="model_name" error={errors.model_name}>
            <Input
              id="model_name"
              value={modelName}
              onChange={(e) => setModelName(e.target.value)}
            />
          </FormField>
          <FormField
            label="LiteLLM Model"
            htmlFor="model"
            hint="Provider prefix (e.g. openai/gpt-4o, azure/gpt-4)"
            error={errors.model}
          >
            <Input
              id="model"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder="openai/gpt-4o"
            />
          </FormField>
          <FormField
            label="API Key (optional)"
            htmlFor="api_key"
            hint="Leave blank to keep the existing key. Provide a new value to rotate."
          >
            <Input
              id="api_key"
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Leave blank to keep existing"
            />
          </FormField>
          <FormField label="API Base (optional)" htmlFor="api_base">
            <Input
              id="api_base"
              value={apiBase}
              onChange={(e) => setApiBase(e.target.value)}
              placeholder="https://api.openai.com/v1"
            />
          </FormField>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button type="button" variant="outline" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}